import { Box, CircularProgress, Typography } from '@mui/material'
import { ReactFlowProvider } from '@xyflow/react'
import ApiClient from '../../backend/ApiClient'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { IFlowRunConfig } from '../../definitions/NodesExecution'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { DnDProvider } from './hooks/DnDContext'
import SettlementFlowEditor from './SettlementFlowEditor'

interface P {
    flowId: string
    eeId?: string
    flowRunConfig?: IFlowRunConfig
}

function FlowInitializer({ flowId, eeId, flowRunConfig }: P) {
    const { status, response: flowResponse } = useQuery({
        queryKey: buildQueryKey('get-flow-' + (flowId)),
        queryFn: () => FlowApiClient.getFlow(flowId, flowRunConfig),
        cache: true,
        enabled: !eeId
    })

    const { status: execEnvStatus, response: execEnv } = useQuery({
        queryKey: buildQueryKey('get-flow-execution-' + eeId),
        queryFn: () => FlowApiClient.getFlowExecutionEnvironment(eeId),
        enabled: !!eeId
    })

    const flow = eeId ? execEnv?.flow : flowResponse

    const { status: approvalStatus, response: approval } = useQuery({
        queryKey: buildQueryKey('get-approval-' + (flow?.uuid)),
        queryFn: () => ApiClient.getEntityApproval(flow?.uuid),
        cache: true,
        enabled: !!flow
    })

    const renderLoading = () => {
        return (
            <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        )
    }

    const renderError = () => (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant='h6' color='error'>
                Something went wrong. Please try again later.
            </Typography>
        </Box>
    )

    const hasError = status === 'error' || execEnvStatus === 'error' || approvalStatus === 'error'
    const loading = status === 'loading' || execEnvStatus === 'loading' || approvalStatus === 'loading' || !flow || !approval

    return (
        <ReactFlowProvider>
            <DnDProvider>
                {hasError
                    ? renderError()
                    : loading
                        ? renderLoading()
                        : (
                                <SettlementFlowEditor
                                    initialFlow={flow}
                                    initialExecEnv={execEnv}
                                    initialEditorMode={execEnv ? 'audit' : 'edit'}
                                    flowRunConfig={flowRunConfig}
                                    approval={approval}
                                />
                            )}
            </DnDProvider>
        </ReactFlowProvider>
    )
}

export default FlowInitializer
